/* eslint-disable camelcase */
import * as React from "react"
import { ViewStyle, TextInput, Appearance } from "react-native"
import { observer } from "mobx-react-lite"
import { FastField, Field, Formik } from "formik"
import _ from "lodash"
import moment from 'moment'

import { color } from "../../../theme"
import { Text } from "../../text/text"
import { translate, TxKeyPath } from "../../../i18n"
import { Currency } from "../../../database"
import { FormControl, FormControlProps } from "../../form-control/form-control"
import { ModelPicker, ModelPickerProps } from "../../model-picker/model-picker"
import { NumeralInput, NumeralInputProps } from "../../input/numeral-input/numeral-input"
import { FormProps } from "../form"
import * as yup from "yup"
import { Checkbox } from "../../checkbox/checkbox"
import { DateTimePickerField } from "../../field/date-time-picker-field/date-time-picker-field"
import { View } from "native-base"

const FORM_DROPDOWN: ViewStyle = {
  paddingHorizontal: 16,
  marginBottom: 8
}

const TEXT_CONTAINER: ViewStyle = {
  paddingHorizontal: 16
}

const ROW_INPUT: ViewStyle = {
  flexDirection: 'row',
  justifyContent: 'space-between',
}

const CHECKBOX_DISCLAIMER: ViewStyle = {
  paddingHorizontal: 16
}

export interface SecondPriceFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    created_at?: string
    currency?: string,
    size_20?: number,
    size_30?: number,
    size_40?: number,
    size_50?: number,
    size_60?: number,
    size_70?: number,
    size_80?: number,
    size_90?: number,
    size_100?: number,
    size_110?: number,
    size_120?: number,
    size_130?: number,
    size_140?: number,
    size_150?: number,
    size_160?: number,
    size_170?: number,
    size_180?: number,
    size_190?: number,
    size_200?: number,
    remark?: string
  }
}


const ModelField = (
  { name, colorScheme = Appearance.getColorScheme(), formControlProps, modelPickerProps }:
  { name: string, colorScheme?: "light" | "dark", formControlProps?: FormControlProps, modelPickerProps?: (Omit<ModelPickerProps, "onValueChange"> & { onValueChange?: (value, index, item) => void }) },
) => (
  <FastField name={name}>
    {({ field, form: { setFieldValue, setFieldTouched }, meta }) => (
      <FormControl
        isInvalid={meta.touched && !!meta.error}
        labelTx={`shrimpPriceScreen.${_.camelCase(field.name)}` as TxKeyPath}
        labelPreset="title3"
        colorScheme={colorScheme}
        labelColor={color.palette.gray800}
        errorMessage={meta.error}
        style={FORM_DROPDOWN}
        {...formControlProps}
      >
        <ModelPicker
          colorScheme={colorScheme}
          placeholderTx={`shrimpPriceScreen.${_.camelCase(field.name)}Placeholder` as TxKeyPath}
          value={field.value}
          onValueChange={(value) => {
            setFieldTouched(field.name)
            setFieldValue(field.name, value)
          }}
          isUseSearch={field.name !== 'species'}
          {...modelPickerProps}
        />
      </FormControl>
    )}
  </FastField>
)

const NumeralField = (
  { name, input = {}, nextInput = {}, colorScheme = Appearance.getColorScheme(), formControlProps, numeralInputProps }:
  { name: string, input: any, nextInput: any, colorScheme?: "light" | "dark", formControlProps?: FormControlProps, numeralInputProps?: NumeralInputProps },
) => (
  <FastField name={name}>
    {({ field, form: { setFieldValue, handleBlur }, meta }) => (
      <FormControl
        isRequired={field.name === 'size_100'}
        isInvalid={meta.touched && !!meta.error}
        labelTx={`shrimpPriceScreen.${_.camelCase(field.name)}` as TxKeyPath}
        colorScheme={colorScheme}
        inputProps={{
          testID: `jala:id/${field.name}-input`,
          autoCapitalize: "none",
          autoComplete: "off",
          autoCorrect: false,
          textContentType: "none",
          keyboardType: "numeric",
          returnKeyType: nextInput[field.name] ? "next" : "go",
          onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : null,
          placeholderTx: 'shrimpPriceScreen.sizePlaceholder' as TxKeyPath,
          defaultValue: field.value,
          onBlur: handleBlur(field.name),
        }}
        errorMessage={translate('shrimpPriceScreen.sizeValidation')}
        style={FORM_DROPDOWN}
        {...formControlProps}
      >
        {(inputProps) => (
          <NumeralInput ref={input[field.name]} colorScheme={colorScheme}
            onChangeNumber={(value) => setFieldValue(field.name, value)} {...inputProps} {...numeralInputProps} />
        )}
      </FormControl>
    )}
  </FastField>
)

const FieldInput = (
  { name, input = {}, nextInput = {}, colorScheme = Appearance.getColorScheme() } :
  { name: string, input: any, nextInput: any, colorScheme?: "light" | "dark"}
) => (
  <Field name={name}>
    {({ field, form: { handleChange, handleBlur, handleSubmit, isValid }, meta }) => {
      return(
        <FormControl
          isInvalid={meta.touched && !!meta.error}
          labelTx={`shrimpPriceScreen.${_.camelCase(field.name)}` as TxKeyPath}
          labelPreset="title3"
          colorScheme={colorScheme}
          inputProps={{
            ref: input[field.name],
            testID: `jala:id/${field.name}-input`,
            autoCorrect: false,
            keyboardType: "default",
            returnKeyType: nextInput[field.name] ? "next" : "go",
            onSubmitEditing: () => nextInput[field.name] ? nextInput[field.name]() : (isValid && handleSubmit()),
            // onSubmitEditing: () => (isValid && handleSubmit()),
            placeholderTx: `shrimpPriceScreen.${_.camelCase(field.name)}Placeholder` as TxKeyPath,
            defaultValue: field.value,
            onChangeText: handleChange(field.name),
            onBlur: handleBlur(field.name),
            multiline: true
          }}
          errorMessage={meta.error}
          style={FORM_DROPDOWN}
        />
      )
      }
    }
  </Field>
)

/**
 * Describe your component here
 */
export const SecondPriceForm = observer(function SecondPriceForm(props: SecondPriceFormProps) {
  const { initialValues = {}, onSubmit, colorScheme = Appearance.getColorScheme(), innerRef, ...rest } = props

  const input = {
    size_100: React.useRef<TextInput>(),
    size_90: React.useRef<TextInput>(),
    size_80: React.useRef<TextInput>(),
    size_70: React.useRef<TextInput>(),
    size_60: React.useRef<TextInput>(),
    size_50: React.useRef<TextInput>(),
    size_40: React.useRef<TextInput>(),
    size_30: React.useRef<TextInput>(),
    size_20: React.useRef<TextInput>(),
    size_110: React.useRef<TextInput>(),
    size_120: React.useRef<TextInput>(),
    size_130: React.useRef<TextInput>(),
    size_140: React.useRef<TextInput>(),
    size_150: React.useRef<TextInput>(),
    size_160: React.useRef<TextInput>(),
    size_170: React.useRef<TextInput>(),
    size_180: React.useRef<TextInput>(),
    size_190: React.useRef<TextInput>(),
    size_200: React.useRef<TextInput>(),
    remark: React.useRef<TextInput>()
  }

  const nextInput = {
    size_100: () => input.size_90.current && input.size_90.current.focus(),
    size_90: () => input.size_80.current && input.size_80.current.focus(),
    size_80: () => input.size_70.current && input.size_70.current.focus(),
    size_70: () => input.size_60.current && input.size_60.current.focus(),
    size_60: () => input.size_50.current && input.size_50.current.focus(),
    size_50: () => input.size_40.current && input.size_40.current.focus(),
    size_40: () => input.size_30.current && input.size_30.current.focus(),
    size_30: () => input.size_20.current && input.size_20.current.focus(),
    size_20: () => input.size_110.current && input.size_110.current.focus(),
    size_110: () => input.size_120.current && input.size_120.current.focus(),
    size_120: () => input.size_130.current && input.size_130.current.focus(),
    size_130: () => input.size_140.current && input.size_140.current.focus(),
    size_140: () => input.size_150.current && input.size_150.current.focus(),
    size_150: () => input.size_160.current && input.size_160.current.focus(),
    size_160: () => input.size_170.current && input.size_170.current.focus(),
    size_170: () => input.size_180.current && input.size_180.current.focus(),
    size_180: () => input.size_190.current && input.size_190.current.focus(),
    size_190: () => input.size_200.current && input.size_200.current.focus(),
    size_200: () => input.remark.current && input.remark.current.focus(),
  }

  const validationSchema = yup.object({
    date: yup.mixed().nullable().required().label(translate("shrimpPriceScreen.date")),
    currency_id: yup.string().required().label(translate("shrimpPriceScreen.currencyId")),
    size_100: yup.number().required().moreThan(0, translate("cantBeZero")).label(translate("validationInput")),
    disclaimer: yup.boolean().required().isTrue().label(''),
    remark: yup.string().ascii().max(255).nullable().label(translate("shrimpPriceScreen.remark")),
  })

  const rowInput = (key1, key2) => (
    <View style={ROW_INPUT}>
      {key1 && <NumeralField
        name={key1}
        colorScheme={colorScheme}
        input={input}
        nextInput={nextInput}
        formControlProps={{ flex: 1 }}
      /> || <View flex={1} paddingX="16px" />}
      {key2 && <NumeralField
        name={key2}
        colorScheme={colorScheme}
        input={input}
        nextInput={nextInput}
        formControlProps={{ flex: 1 }}
      /> || <View flex={1} paddingX="16px" />}
    </View>
  )

  return (
    <Formik
      enableReinitialize
      initialValues={{
        date: moment().format('YYYY-MM-DD'),
        currency_id: "IDR",
        size_20: null,
        size_30: null,
        size_40: null,
        size_50: null,
        size_60: null,
        size_70: null,
        size_80: null,
        size_90: null,
        size_100: 0,
        size_110: null,
        size_120: null,
        size_130: null,
        size_140: null,
        size_150: null,
        size_160: null,
        size_170: null,
        size_180: null,
        size_190: null,
        size_200: null,
        remark: null,
        disclaimer: null,
        ...initialValues
      }}
      onSubmit={onSubmit}
      innerRef={innerRef}
      validationSchema={validationSchema}
      {...rest}
    >
       {({ values, initialValues}) => {
        return(
          <>
            <DateTimePickerField
              name="date"
              txPrefix="shrimpPriceScreen"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true  }}
              // formControlProps={{ isRequired: true  }}
            />
            <ModelField
              name="currency_id"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
              modelPickerProps={{
                Model: Currency,
                fetchData: (model, search) => (
                  model.fetch({
                    search,
                    sort: 'name'
                  }).then((models) => (
                    _.sortBy(models, (m) => {
                      const index = ['IDR', 'USD', 'VND'].findIndex((k) => k === m.id)
                      return index < 0 ? m.name : `${index}`
                    })
                  ))
                ),
                renderSelectedItemText: (item) => item && `(${item.symbol}) ${item.name}`
              }}
            />
            <View style={TEXT_CONTAINER}>
              <Text preset="title3" tx="shrimpPriceScreen.sizeInput" color={color.palette.gray800} />
            </View>
            {rowInput('size_100', 'size_200')}
            {rowInput('size_90', 'size_190')}
            {rowInput('size_80', 'size_180')}
            {rowInput('size_70', 'size_170')}
            {rowInput('size_60', 'size_160')}
            {rowInput('size_50', 'size_150')}
            {rowInput('size_40', 'size_140')}
            {rowInput('size_30', 'size_130')}
            {rowInput('size_20', 'size_120')}
            {rowInput(null, 'size_110')}
            <FieldInput name="remark" colorScheme={colorScheme} input={input} nextInput={nextInput} />
            <FastField name="disclaimer">
              {({ field, form: { setFieldValue } }) => (
                <Checkbox
                  value={field.value}
                  onToggle={(value) => setFieldValue(field.name, value)}
                  text={translate("shrimpPriceScreen.disclaimer")}
                  multiline
                  colorScheme={colorScheme}
                  labelPreset="title5"
                  outlineStyle={{ borderColor: color.palette.gray700 }}
                  style={CHECKBOX_DISCLAIMER}
                />
              )}
            </FastField>
          </>
        )
       }}
    </Formik>
  )
})

SecondPriceForm.displayName = 'SecondPriceForm'
